import React from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowDownward,
  ArrowUpward,
  Close,
  ContentCopy,
  DeleteOutline,
} from '@mui/icons-material';
import { conditionSummary, IEditorNode, NODE_KIND_LABEL } from './editorModel';
import { reasonExplanation } from './migrationModel';
import { nodeAt } from './readingModel';
import {
  migrationLabel,
  plainTextOfParagraphs,
  paragraphsOfNode,
} from './richTextModel';
import {
  alternativeLine,
  elementText,
  previewVerdict,
  ReaderContext,
  ReaderSelection,
} from './SectionReader';
import { TechnicalDetails } from './TechnicalDetails';

interface Props {
  selection: ReaderSelection | null;
  nodes: IEditorNode[];
  context: ReaderContext;
  editable: boolean;
  /** Der vorhandene Editor für den Knoten an dieser Stelle der Abschnittsliste. */
  editorFor: (_index: number) => React.ReactNode;
  onMove: (_index: number, _delta: number) => void;
  onDuplicate: (_index: number) => void;
  onRemove: (_index: number) => void;
  onClose: () => void;
}

const Verdict: React.FC<{ verdict: boolean | null }> = ({ verdict }) =>
  verdict === null ? null : (
    <Chip
      size='small'
      variant='outlined'
      color={verdict ? 'success' : 'default'}
      label={verdict ? 'trifft zu' : 'trifft nicht zu'}
      sx={{ ml: 1 }}
    />
  );

const sampleText = (value: string | null | undefined): string =>
  value === undefined
    ? 'Oben einen Vorschauvorgang wählen, um den Wert zu sehen.'
    : value === null || value === ''
      ? 'Kein Wert im Vorschauvorgang — in der Urkunde steht dann eine offene Stelle.'
      : value;

export const ReaderDetails: React.FC<Props> = ({
  selection,
  nodes,
  context,
  editable,
  editorFor,
  onMove,
  onDuplicate,
  onRemove,
  onClose,
}) => {
  const top = selection ? selection.path[0] : -1;
  const node = selection ? nodeAt(nodes, selection.path) : null;

  let title = '';
  let body: React.ReactNode = null;
  if (selection?.kind === 'field') {
    const name =
      context.fieldLabel(selection.selector) ?? selection.label ?? 'Datenfeld';
    title = 'Datenfeld';
    body = (
      <Stack spacing={1}>
        <Typography variant='subtitle1'>{name}</Typography>
        <Typography variant='body2' color='text.secondary'>
          Aktueller Wert
        </Typography>
        <Typography variant='body1'>
          {sampleText(context.sample(selection.selector))}
        </Typography>
        <TechnicalDetails
          entries={[{ label: 'Selektor', value: selection.selector }]}
        />
      </Stack>
    );
  } else if (selection?.kind === 'choice') {
    title = 'Bedingter Text im Satz';
    const verdicts = selection.alternatives.map(alternative =>
      previewVerdict(alternative.condition, context)
    );
    const chosen = selection.alternatives.find(
      (_, index) => verdicts[index] === true
    );
    body = (
      <Stack spacing={1}>
        {selection.alternatives.map((alternative, index) => (
          <Box
            key={index}
            sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
          >
            <Typography variant='body2'>
              {alternativeLine(alternative, context)}
            </Typography>
            <Verdict verdict={verdicts[index]} />
          </Box>
        ))}
        {selection.alternatives.length === 1 && (
          <Typography variant='body2' color='text.secondary'>
            Trifft die Bedingung nicht zu, steht an dieser Stelle nichts.
          </Typography>
        )}
        {chosen && (
          <Typography variant='body2'>
            Mit dem Vorschauvorgang steht hier: „{chosen.text.trim()}"
          </Typography>
        )}
      </Stack>
    );
  } else if (node) {
    title = NODE_KIND_LABEL[node.type] ?? 'Element';
    if (node.type === 'migration_required') {
      body = (
        <Stack spacing={1}>
          <Typography variant='body2'>
            Diese Stelle konnte beim Import nicht sicher übernommen werden.
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {reasonExplanation(node.reasonCode ?? '')}
          </Typography>
          <TechnicalDetails
            entries={[
              { label: 'Grund', value: node.reasonCode },
              { label: 'Hinweis', value: node.reason },
            ]}
          />
        </Stack>
      );
    } else if (node.type === 'text' || node.type === 'conditional_text') {
      const open = paragraphsOfNode(node)
        .flatMap(paragraph => paragraph.runs)
        .filter(run => run.migration);
      body = (
        <Stack spacing={1}>
          {node.type === 'conditional_text' && (
            <Box
              sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
            >
              <Typography variant='body2'>
                {conditionSummary(
                  node.condition,
                  context.questions,
                  context.fieldLabel
                ) || 'Ohne Bedingung'}
              </Typography>
              <Verdict verdict={previewVerdict(node.condition, context)} />
            </Box>
          )}
          {open.length > 0 && (
            <Typography variant='body2' color='error.main'>
              {open.length === 1 ? 'Eine Stelle' : `${open.length} Stellen`} in
              diesem Text konnte
              {open.length === 1 ? '' : 'n'} beim Import nicht sicher übernommen
              werden (
              {open.map(run => migrationLabel(run.migration)).join(', ')}).
            </Typography>
          )}
          {node.type === 'conditional_text' &&
            (node.nodes ?? []).length === 0 && (
              <Typography variant='body2' color='text.secondary'>
                {plainTextOfParagraphs(paragraphsOfNode(node)).slice(0, 400)}
              </Typography>
            )}
        </Stack>
      );
    } else {
      body = (
        <Typography variant='body2'>{elementText(node, context)}</Typography>
      );
    }
  }

  return (
    <Drawer
      anchor='right'
      open={Boolean(selection)}
      onClose={onClose}
      // Über der Kopfleiste der Anwendung (drawer + 1), aber unter Dialogen, Menüs und Auswahllisten
      // (modal): mit modal + 1 lagen der Dialog »Bedingter Text im Satz« und jede Auswahlliste im
      // Detailbereich darunter — für ein Skript klickbar, für einen Menschen verdeckt.
      sx={{ zIndex: theme => theme.zIndex.drawer + 2 }}
      PaperProps={
        {
          sx: { width: { xs: '100%', sm: 560 }, p: 2 },
          'data-testid': 'reader-details',
        } as object
      }
    >
      {selection && (
        <Stack spacing={2}>
          <Stack direction='row' alignItems='center' spacing={1}>
            <Typography variant='h6' sx={{ flexGrow: 1 }}>
              {title}
            </Typography>
            <IconButton aria-label='Schließen' onClick={onClose}>
              <Close />
            </IconButton>
          </Stack>
          {body}
          <Divider />
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography variant='subtitle2' sx={{ flexGrow: 1 }}>
              {selection.path.length > 1
                ? 'Bearbeiten (im umgebenden Element)'
                : 'Bearbeiten'}
            </Typography>
            <IconButton
              size='small'
              aria-label='Nach oben'
              disabled={!editable || top === 0}
              onClick={() => onMove(top, -1)}
            >
              <ArrowUpward fontSize='inherit' />
            </IconButton>
            <IconButton
              size='small'
              aria-label='Nach unten'
              disabled={!editable || top === nodes.length - 1}
              onClick={() => onMove(top, 1)}
            >
              <ArrowDownward fontSize='inherit' />
            </IconButton>
            <IconButton
              size='small'
              aria-label='Duplizieren'
              disabled={!editable}
              onClick={() => onDuplicate(top)}
            >
              <ContentCopy fontSize='inherit' />
            </IconButton>
            <IconButton
              size='small'
              color='error'
              aria-label='Löschen'
              disabled={!editable}
              onClick={() => onRemove(top)}
            >
              <DeleteOutline fontSize='inherit' />
            </IconButton>
          </Stack>
          {selection.kind === 'choice' &&
          new Set(
            selection.alternatives.map(alternative => alternative.path[0])
          ).size > 1 ? (
            // Zweige als eigene Knoten (Klon): je Zweig sein Editor. Ein bedingter Text im Satz
            // (ein Lauf) hat nur einen — den seines Absatzes.
            <Stack spacing={2}>
              {Array.from(
                new Set(
                  selection.alternatives.map(alternative => alternative.path[0])
                )
              ).map((index, position) => (
                <Box key={index}>
                  <Typography variant='caption' color='text.secondary'>
                    Zweig {position + 1}
                  </Typography>
                  {editorFor(index)}
                </Box>
              ))}
            </Stack>
          ) : (
            editorFor(top)
          )}
          {!editable && (
            <Typography variant='body2' color='text.secondary'>
              Diese Version ist veröffentlicht und nur lesbar.
            </Typography>
          )}
          <Button onClick={onClose}>Fertig</Button>
        </Stack>
      )}
    </Drawer>
  );
};
